import {
  PetVeterinaryInformationFragment,
  UpdateVetPracticeDetailsMutationFn,
  useUpdateVetPracticeDetailsMutation,
} from '@embark/shared-components/src/generated/graphql-client-types';
import { UseFormHandleSubmit, UseFormReturn, useForm } from 'react-hook-form';

import { GOOGLE_MAPS_API_KEY } from 'projects/legacyClient/src/js/browser-api-keys';
import { HeapCustomEvent } from 'clientServerShared/types/eventTracking';
import { Loader } from '@googlemaps/js-api-loader';
import { LocationAutocompleteProps } from '@embark/shared-components/src/locationAutocomplete/locationAutocomplete';
import { LocationAutocompleteResult } from '@embark/shared-components/src/locationAutocomplete/locationAutocompleteHooks';
import { graphqlErrorMessages } from 'clientServerShared/types/graphqlErrors';
import { showCommonErrorSnackbar } from '@embark/shared-components/src/services/snackbar/snackbar';
import { useFetchGoogleLocationPrediction } from '@embark/shared-components/src/locationAutocomplete/locationAutocompleteHooks';
import { useRef } from 'react';
import { useSnackbar } from 'notistack';

type UseEditVetInformationFormReturn = {
  onChangeAddress: LocationAutocompleteProps['onChange'];
};

type UseVetLocationProps = {
  setModalOpen: () => void;
  embarkMayContactVet: PetVeterinaryInformationFragment['embarkMayContactVet'] | null;
  practiceLocation: PetVeterinaryInformationFragment['practiceLocation'] | null;
  open: boolean;
};

export interface VetLocationForm {
  embarkMayContactVet: boolean | null;
  practiceLocation: LocationAutocompleteResult | null;
}

type UseVetInformationReturn = {
  formInformation: UseFormReturn<VetLocationForm, object>;
  handleSubmit: UseFormHandleSubmit<VetLocationForm>;
  handleClose: () => void;
  placesServiceRef: React.RefObject<HTMLDivElement>;
  updateVetPracticeDetails: UpdateVetPracticeDetailsMutationFn;
  onChangeAddress: LocationAutocompleteProps['onChange'];
};

export function useEditVetInformationModal(props: UseVetLocationProps): UseVetInformationReturn {
  const { setModalOpen, embarkMayContactVet, practiceLocation, open } = props;
  const { data: options } = useFetchGoogleLocationPrediction(practiceLocation?.name ?? undefined);
  const { enqueueSnackbar } = useSnackbar();
  const [updateVetPracticeDetails, {}] = useUpdateVetPracticeDetailsMutation({
    onError: (error) => {
      if (error.message === graphqlErrorMessages.NotAuthorized) {
        enqueueSnackbar(`Failed to update information: ${error.message}`, {
          variant: 'error',
        });
      } else {
        showCommonErrorSnackbar(enqueueSnackbar);
      }
    },
    onCompleted: () => {
      setModalOpen();
      location.reload();
    },
  });
  const placesServiceRef = useRef<HTMLDivElement>(null);

  const handleClose = async (): Promise<void> => {
    setModalOpen();
  };

  const formInformation = useForm<VetLocationForm>({
    mode: 'onChange',
    defaultValues: {
      embarkMayContactVet: embarkMayContactVet ?? true,
      practiceLocation: options[0],
    },
  });

  const { setValue, handleSubmit } = formInformation;

  if (open) {
    const loader = new Loader({
      apiKey: GOOGLE_MAPS_API_KEY,
      libraries: ['places'],
      retries: 3,
    });

    loader
      .load()
      .then((google) => {
        const placesService = new google.maps.places.PlacesService(placesServiceRef.current as HTMLDivElement);
        placesService?.getDetails({ placeId: options[0]?.place_id }, (result) => {
          const formKey = 'practiceLocation';
          setValue(formKey, { ...options[0], details: result });
          window.heap?.track(HeapCustomEvent.GOOGLE_MAPS_API_PLACES_REQUEST, {
            formKey,
            requestType: 'google.maps.places.PlacesService.getDetails',
            loadingContext: 'editVeterinaryInformationModalHooks',
          });
        });
      })
      .catch(() => {
        return;
      });
  }

  function useAddressFormInput(): UseEditVetInformationFormReturn {
    const onChangeAddress: LocationAutocompleteProps['onChange'] = (_event, newValue): void => {
      if (newValue) {
        setValue('practiceLocation', newValue);
      }
    };

    return {
      onChangeAddress,
    };
  }

  const { onChangeAddress } = useAddressFormInput();
  return {
    formInformation,
    handleSubmit,
    handleClose,
    placesServiceRef,
    updateVetPracticeDetails,
    onChangeAddress,
  };
}
